import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Badge,
  Button,
  Table,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import DataTable from "react-data-table-component";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import AdStatsService from "../../../services/OrderingServices/AdStatsService";

const DeviceStats = () => {
  const navigate = useNavigate();
  const { macId } = useParams();
  const [deviceStats, setDeviceStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });

  const customStyles = {
    headCells: {
      style: {
        fontSize: "0.92rem",
        fontWeight: 610,
        backgroundColor: "#f8f9fa",
      },
    },
    rows: { style: { fontSize: "0.9rem" } },
  };

  useEffect(() => {
    if (macId) {
      fetchDeviceStats();
    }
  }, [macId]);

  async function fetchDeviceStats() {
    try {
      setLoading(true);
      const response = await AdStatsService.getDeviceViews({
        macId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      if (response) {
        setDeviceStats(response.data || response);
      }
    } catch (error) {
      toast.error("Failed to fetch device statistics");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const handleDateRangeChange = () => {
    fetchDeviceStats();
  };

  const adViewsColumns = [
    {
      name: "Ad ID",
      selector: (row) => row.adId || "-",
      sortable: true,
      width: "80px",
    },
    {
      name: "Ad Title",
      selector: (row) => row.adTitle || row.title || "-",
      sortable: true,
      width: "200px",
    },
    {
      name: "Views",
      selector: (row) => row.viewCount || row.views || 0,
      sortable: true,
      width: "100px",
    },
    {
      name: "Completed",
      selector: (row) => (
        <Badge color={row.wasCompleted ? "success" : "danger"}>
          {row.wasCompleted ? "Yes" : "No"}
        </Badge>
      ),
      sortable: false,
      width: "100px",
    },
    {
      name: "Skipped",
      selector: (row) => (
        <Badge color={row.wasSkipped ? "warning" : "info"}>
          {row.wasSkipped ? "Yes" : "No"}
        </Badge>
      ),
      sortable: false,
      width: "100px",
    },
    {
      name: "Visible (Sec)",
      selector: (row) => row.visibleSeconds || 0,
      sortable: true,
      width: "110px",
    },
    {
      name: "Played (Sec)",
      selector: (row) => row.playedSeconds || 0,
      sortable: true,
      width: "110px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <Button
          color="info"
          outline
          size="sm"
          onClick={() => navigate(`/ads/stats/ad-summary/${row.adId}`)}
          title="View Ad Details"
        >
          <i className="bx bx-detail"></i>
        </Button>
      ),
      width: "90px",
    },
  ];

  if (loading && !deviceStats) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "50px" }}>
        <Triangle color="#435ebe" height={80} width={80} />
      </div>
    );
  }

  if (!deviceStats) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Device Statistics" breadcrumbItem="Device Stats" />
          <Row>
            <Col lg="12">
              <Card>
                <CardBody className="text-center">
                  <p>Device statistics not found</p>
                  <Button color="primary" onClick={() => navigate("/ads/daily-stats")}>
                    Back to Dashboard
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  const statsData = Array.isArray(deviceStats) ? deviceStats : deviceStats.data || [];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="Device Statistics"
            breadcrumbItem="Device Performance"
          />

          {/* Device Header */}
          <Row className="mb-4">
            <Col lg="12">
              <Card className="border-0 shadow-sm">
                <CardBody>
                  <Row className="align-items-center">
                    <Col md="8">
                      <h3>Device Performance Analytics</h3>
                      <p className="text-muted mb-0">
                        MAC ID: <span className="font-monospace fw-bold">{macId}</span>
                      </p>
                    </Col>
                    <Col md="4" className="text-end">
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        onClick={() => navigate("/ads/daily-stats")}
                      >
                        <i className="bx bx-arrow-back me-2"></i>Back
                      </Button>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Key Metrics */}
          <Row className="mb-4">
            <Col md="3">
              <Card className="border-0 shadow-sm text-center">
                <CardBody>
                  <h6 className="text-muted mb-2">Total Ads Displayed</h6>
                  <h2 className="text-primary mb-0">{statsData.length || 0}</h2>
                </CardBody>
              </Card>
            </Col>
            <Col md="3">
              <Card className="border-0 shadow-sm text-center">
                <CardBody>
                  <h6 className="text-muted mb-2">Completions</h6>
                  <h2 className="text-success mb-0">
                    {statsData.filter((s) => s.wasCompleted).length || 0}
                  </h2>
                </CardBody>
              </Card>
            </Col>
            <Col md="3">
              <Card className="border-0 shadow-sm text-center">
                <CardBody>
                  <h6 className="text-muted mb-2">Skips</h6>
                  <h2 className="text-warning mb-0">
                    {statsData.filter((s) => s.wasSkipped).length || 0}
                  </h2>
                </CardBody>
              </Card>
            </Col>
            <Col md="3">
              <Card className="border-0 shadow-sm text-center">
                <CardBody>
                  <h6 className="text-muted mb-2">Completion Rate</h6>
                  <h2 className="text-info mb-0">
                    {statsData.length > 0
                      ? (
                          (statsData.filter((s) => s.wasCompleted).length /
                            statsData.length) *
                          100
                        ).toFixed(1)
                      : 0}
                    %
                  </h2>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Ads Viewed by Device */}
          <Row>
            <Col lg="12">
              <Card className="border-0 shadow-sm">
                <CardHeader className="bg-light">
                  <Row className="align-items-center">
                    <Col md="4">
                      <h5 className="mb-0">Ads Displayed on Device</h5>
                    </Col>
                    <Col md="8">
                      <Row>
                        <Col md="5">
                          <FormGroup>
                            <Label for="startDate" className="small">
                              Start Date
                            </Label>
                            <Input
                              type="date"
                              name="startDate"
                              id="startDate"
                              value={dateRange.startDate}
                              onChange={(e) =>
                                setDateRange({
                                  ...dateRange,
                                  startDate: e.target.value,
                                })
                              }
                            />
                          </FormGroup>
                        </Col>
                        <Col md="5">
                          <FormGroup>
                            <Label for="endDate" className="small">
                              End Date
                            </Label>
                            <Input
                              type="date"
                              name="endDate"
                              id="endDate"
                              value={dateRange.endDate}
                              onChange={(e) =>
                                setDateRange({
                                  ...dateRange,
                                  endDate: e.target.value,
                                })
                              }
                            />
                          </FormGroup>
                        </Col>
                        <Col md="2" className="d-flex align-items-end">
                          <Button
                            color="primary"
                            onClick={handleDateRangeChange}
                            disabled={loading}
                            className="w-100"
                          >
                            {loading ? "Loading..." : "Filter"}
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <Triangle color="#435ebe" height={80} width={80} />
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <DataTable
                        columns={adViewsColumns}
                        data={statsData}
                        pagination
                        customStyles={customStyles}
                        noDataComponent={
                          <div style={{ padding: "20px" }}>
                            No ads viewed in the selected date range
                          </div>
                        }
                        defaultSortFieldId="viewCount"
                        defaultSortAsc={false}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DeviceStats;
